/**
 * Field Validators
 *
 * Pure validation functions shared across auth forms and deposit/withdrawal flows.
 * Every function takes a value (+ optional rules/config) and returns a uniform result:
 *   { valid: boolean|null, message: string, type: 'valid'|'error'|'warning'|'neutral' }
 *
 * No DOM manipulation — this module is purely functional.
 *
 * @package betbeast
 */

// ─── Email ───────────────────────────────────────────────────────────────────

/**
 * Validate an email address.
 *
 * @param {string} value   Trimmed input value.
 * @param {Object} [rules] Rules from accountData.validationRules.email
 * @returns {{ valid: boolean, message: string, type: string }}
 */
export function validateEmail(value, rules = {}) {
    const messages  = rules.error_messages || {};
    const maxLength = rules.max_length || 254;

    if (!value) {
        return { valid: false, message: messages.required || 'Email address is required.', type: 'error' };
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return { valid: false, message: messages.invalid || 'Please enter a valid email address.', type: 'warning' };
    }

    if (value.length > maxLength) {
        return { valid: false, message: messages.max_length || 'Email address is too long.', type: 'warning' };
    }

    return { valid: true, message: '', type: 'valid' };
}

// ─── Password ────────────────────────────────────────────────────────────────

/**
 * Validate a password.
 *
 * @param {string}      value   Trimmed input value.
 * @param {Object}      [rules] Rules from accountData.validationRules.password
 * @param {Object|null} [attrs] Optional HTML attribute overrides { minlength, maxlength }.
 * @returns {{ valid: boolean, message: string, type: string }}
 */
export function validatePassword(value, rules = {}, attrs = null) {
    const messages = rules.error_messages || {};

    let minLength = rules.hasOwnProperty('min_length') ? rules.min_length : null;
    let maxLength = rules.hasOwnProperty('max_length') ? rules.max_length : null;

    if (minLength === null && attrs && attrs.minlength !== undefined) {
        minLength = parseInt(attrs.minlength, 10);
    }
    if (maxLength === null && attrs && attrs.maxlength !== undefined) {
        maxLength = parseInt(attrs.maxlength, 10);
    }

    const requireLetter      = rules.hasOwnProperty('require_letter')      ? rules.require_letter      : false;
    const requireNumber      = rules.hasOwnProperty('require_number')      ? rules.require_number      : false;
    const requireUppercase   = rules.hasOwnProperty('require_uppercase')   ? rules.require_uppercase   : false;
    const requireLowercase   = rules.hasOwnProperty('require_lowercase')   ? rules.require_lowercase   : false;
    const requireSpecial     = rules.hasOwnProperty('require_special')     ? rules.require_special     : false;
    const specialChars       = rules.special_chars || '-!@$^&*';
    const disallowAllDigits  = rules.hasOwnProperty('disallow_all_digits') ? rules.disallow_all_digits : false;

    if (!value) {
        return { valid: false, message: messages.required || 'Password is required.', type: 'error' };
    }

    if (minLength !== null && value.length < minLength) {
        const msg = (messages.min_length || 'Password must be at least {min} characters.').replace('{min}', minLength);
        return { valid: false, message: msg, type: 'warning' };
    }

    if (maxLength !== null && value.length > maxLength) {
        const msg = (messages.max_length || 'Password must not exceed {max} characters.').replace('{max}', maxLength);
        return { valid: false, message: msg, type: 'warning' };
    }

    if (requireLetter && !/[a-zA-Z]/.test(value)) {
        return { valid: false, message: messages.require_letter || 'Password must contain at least one letter.', type: 'warning' };
    }

    if (requireUppercase && !/[A-Z]/.test(value)) {
        return { valid: false, message: messages.require_uppercase || 'Password must contain at least one uppercase letter.', type: 'warning' };
    }

    if (requireLowercase && !/[a-z]/.test(value)) {
        return { valid: false, message: messages.require_lowercase || 'Password must contain at least one lowercase letter.', type: 'warning' };
    }

    if (requireNumber && !/[0-9]/.test(value)) {
        return { valid: false, message: messages.require_number || 'Password must contain at least one number.', type: 'warning' };
    }

    if (disallowAllDigits && /^\d+$/.test(value)) {
        return { valid: false, message: messages.all_digits || 'Password cannot contain only numbers.', type: 'warning' };
    }

    if (requireSpecial) {
        const escaped = specialChars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
        if (!new RegExp('[' + escaped + ']').test(value)) {
            const msg = (messages.require_special || 'Password must contain at least one special character ({chars}).').replace('{chars}', specialChars);
            return { valid: false, message: msg, type: 'warning' };
        }
    }

    return { valid: true, message: '', type: 'valid' };
}

// ─── Phone ───────────────────────────────────────────────────────────────────

/**
 * Validate a phone number.
 *
 * @param {string} value   Trimmed input value (digits only or with formatting).
 * @param {Object} [rules] Rules from accountData.validationRules.phone
 * @returns {{ valid: boolean, message: string, type: string }}
 */
export function validatePhone(value, rules = {}) {
    const messages  = rules.error_messages || {};
    const minDigits = rules.min_digits || 8;
    const maxDigits = rules.max_digits || 15;

    if (!value) {
        return { valid: false, message: messages.required || 'Phone number is required.', type: 'error' };
    }

    const digitsOnly = value.replace(/\D/g, '');

    if (digitsOnly.length === 0) {
        return { valid: false, message: messages.invalid || 'Please enter a valid phone number.', type: 'warning' };
    }

    if (digitsOnly.length < minDigits) {
        const msg = (messages.min_digits || 'Phone number must have at least {min} digits.').replace('{min}', minDigits);
        return { valid: false, message: msg, type: 'warning' };
    }

    if (digitsOnly.length > maxDigits) {
        const msg = (messages.max_digits || 'Phone number must not exceed {max} digits.').replace('{max}', maxDigits);
        return { valid: false, message: msg, type: 'warning' };
    }

    return { valid: true, message: '', type: 'valid' };
}

// ─── Birthdate ───────────────────────────────────────────────────────────────

/**
 * Calculate age from a Date object.
 *
 * @param {Date} birthDate
 * @returns {number}
 */
export function calculateAge(birthDate) {
    const today    = new Date();
    let age        = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
}

/**
 * Validate a birthdate string (YYYY-MM-DD).
 *
 * @param {string} value   Date string.
 * @param {Object} [rules] Rules from accountData.validationRules.birthdate
 * @returns {{ valid: boolean, message: string, type: string }}
 */
export function validateBirthdate(value, rules = {}) {
    const messages = rules.error_messages || {};
    const minAge   = rules.min_age || 18;
    const maxAge   = rules.max_age || 120;

    if (!value || value.trim() === '') {
        return { valid: false, message: messages.required || 'Date of birth is required.', type: 'error' };
    }

    const birthDate = new Date(value);
    if (isNaN(birthDate.getTime())) {
        return { valid: false, message: messages.invalid || 'Please enter a valid date.', type: 'warning' };
    }

    if (birthDate > new Date()) {
        return { valid: false, message: messages.future || 'Date of birth cannot be in the future.', type: 'warning' };
    }

    const age = calculateAge(birthDate);

    if (age > maxAge) {
        return { valid: false, message: messages.max_age || messages.invalid || 'Please enter a valid date of birth.', type: 'warning' };
    }

    if (age < minAge) {
        const msg = (messages.min_age || 'You must be at least {age} years old to register.').replace('{age}', minAge);
        return { valid: false, message: msg, type: 'warning' };
    }

    return { valid: true, message: '', type: 'valid' };
}

// ─── Name ────────────────────────────────────────────────────────────────────

/**
 * Validate a name field (first_name / last_name).
 *
 * @param {string} value
 * @param {Object} [rules] Rules from accountData.validationRules.name
 * @returns {{ valid: boolean, message: string, type: string }}
 */
export function validateName(value, rules = {}) {
    const messages  = rules.error_messages || {};
    const minLength = rules.min_length || 2;
    const maxLength = rules.max_length || 50;

    if (!value) {
        return { valid: false, message: messages.required || 'This field is required.', type: 'error' };
    }

    if (value.length < minLength) {
        const msg = (messages.min_length || 'Must be at least {min} characters.').replace('{min}', minLength);
        return { valid: false, message: msg, type: 'warning' };
    }

    if (value.length > maxLength) {
        const msg = (messages.max_length || 'Must not exceed {max} characters.').replace('{max}', maxLength);
        return { valid: false, message: msg, type: 'warning' };
    }

    return { valid: true, message: '', type: 'valid' };
}

// ─── Address ─────────────────────────────────────────────────────────────────

/**
 * Validate an address-related field (street, city, postal).
 *
 * @param {string} value
 * @param {string} fieldType  'street' | 'city' | 'postal'
 * @param {Object} [rules]    Rules from accountData.validationRules.address
 * @returns {{ valid: boolean, message: string, type: string }}
 */
export function validateAddress(value, fieldType, rules = {}) {
    const messages = rules.error_messages || {};

    // Postal may be optional
    if (fieldType === 'postal' && !rules.postal_required) {
        if (!value) {
            return { valid: true, message: '', type: 'valid' };
        }
    }

    if (!value && (fieldType !== 'postal' || rules.postal_required)) {
        const msgKey = fieldType + '_required';
        return { valid: false, message: messages[msgKey] || 'This field is required.', type: 'error' };
    }

    let minLength = 2;
    let msgKey    = fieldType + '_length';

    if (fieldType === 'street') {
        minLength = rules.street_min_length || 5;
    } else if (fieldType === 'city') {
        minLength = rules.city_min_length || 2;
    } else if (fieldType === 'postal') {
        minLength = rules.postal_min_length || 3;
        msgKey    = 'postal_too_short';
    }

    if (value && value.length < minLength) {
        const msg = (messages[msgKey] || 'Must be at least {min} characters.').replace('{min}', minLength);
        return { valid: false, message: msg, type: 'warning' };
    }

    return { valid: true, message: '', type: 'valid' };
}

// ─── Country ─────────────────────────────────────────────────────────────────

/**
 * Validate a country selection.
 *
 * @param {string} value  Country code or "CC:CUR" composite.
 * @param {Object} [rules]
 * @returns {{ valid: boolean, message: string, type: string }}
 */
export function validateCountry(value, rules = {}) {
    const messages = rules.error_messages || {};

    if (!value) {
        return { valid: false, message: messages.required || 'Please select your country.', type: 'error' };
    }

    let countryCode = value;
    if (value.indexOf(':') !== -1) {
        countryCode = value.split(':')[0];
    }

    if (countryCode.length !== 2) {
        return { valid: false, message: messages.invalid || 'Please select a valid country.', type: 'warning' };
    }

    return { valid: true, message: '', type: 'valid' };
}

// ─── Amount (deposit / withdrawal) ──────────────────────────────────────────

/**
 * Validate a numeric amount against min/max limits and optional balance.
 *
 * @param {number}  amount
 * @param {Object}  options
 * @param {number}  options.min               Minimum allowed amount.
 * @param {number}  options.max               Maximum allowed amount.
 * @param {number}  [options.availableBalance] Available balance (withdrawal only).
 * @param {string}  [options.label]           "deposit" or "withdrawal" — used in messages.
 * @returns {{ valid: boolean, message: string, type: string }}
 */
export function validateAmount(amount, { min, max, availableBalance, label = 'deposit' } = {}) {
    if (isNaN(amount)) {
        return { valid: false, message: 'Please enter a valid amount', type: 'error' };
    }

    if (amount < min) {
        return { valid: false, message: `Minimum ${label} amount is ${min}`, type: 'warning' };
    }

    if (amount > max) {
        return { valid: false, message: `Maximum ${label} amount is ${max}`, type: 'warning' };
    }

    if (availableBalance !== undefined && amount > availableBalance) {
        return { valid: false, message: `Insufficient funds. Your available balance is ${availableBalance}`, type: 'warning' };
    }

    return { valid: true, message: '', type: 'valid' };
}

// ─── Input formatters / normalizers ─────────────────────────────────────────

/**
 * Normalize an email input value (trim + optional lowercase).
 *
 * @param {HTMLInputElement} input
 * @param {Object}           [rules] Rules from accountData.validationRules.email
 */
export function normalizeEmail(input, rules = {}) {
    if (rules.normalize_lowercase !== false) {
        input.value = input.value.trim().toLowerCase();
    } else {
        input.value = input.value.trim();
    }
}

/**
 * Strip non-digit characters from a phone input while preserving cursor position.
 *
 * @param {HTMLInputElement} input
 */
export function formatPhoneInput(input) {
    const cursorPos      = input.selectionStart;
    const originalLength = input.value.length;

    input.value = input.value.replace(/\D/g, '');

    const diff        = originalLength - input.value.length;
    const newCursorPos = Math.max(0, cursorPos - diff);
    input.setSelectionRange(newCursorPos, newCursorPos);
}
